import React, { useCallback, useEffect, useState } from 'react';
import { Box, Divider, IconButton, List, ListItem, Pagination, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import AddCircle from '@mui/icons-material/AddCircle';
import { useConfirm } from 'material-ui-confirm';

import PlatoonGroup from '../../../entities/platoons/PlatoonGroup/PlatoonGroup';
import EditButton from '../../../shared/ui/EditButton/EditButton';
import DeleteButton from '../../../shared/ui/DeleteButton/DeleteButton';
import NoData from '../../../shared/ui/NoData/NoData';
import PendingData from '../../../shared/ui/PendingData/PendingData';

import {
  useDeletePlatoonGroupMutation,
  useLazyGetPlatoonGroupsQuery,
} from '../../../entities/platoons/platoonGroupsResource/redux/platoonGroupsResource.api';

import { getPlatoonGroupVehicles, getVehiclesWithDtoParams } from '../../../shared/api/api';

import convertDurationToSeconds from '../../../shared/utils/convertDurationToSeconds';
import { VEHICLE_GROUP_UPDATE_INTERVAL_MS } from '../../../entities/platoons/constants';

import style from './CompanyPlatoonGroupsWidget.module.css';

const pageSize = 5;

function CompanyPlatoonGroupsWidget({ company, toGroup, toCreateGroup }) {
  // eslint-disable-next-line no-undef
  const token = window.localStorage.getItem('token') || window.sessionStorage.getItem('token');
  const confirm = useConfirm();

  // Количество страниц
  const [countPages, setCountPages] = useState(1);
  // Текущая страница
  const [page, setPage] = useState(1);
  // Отображаемые группы
  const [platoonGroups, setPlatoonGroups] = useState([]);
  const [displayedGroups, setDisplayedGroups] = useState([]);

  const [getGroups, { data, isLoading, isError }] = useLazyGetPlatoonGroupsQuery({
    pollingInterval: VEHICLE_GROUP_UPDATE_INTERVAL_MS,
  });

  const handleChangePage = (_, value) => {
    setPage(value);
  };

  const [deletePlatoonGroup] = useDeletePlatoonGroupMutation();
  const onDelete = useCallback(
    ({ id }) =>
      deletePlatoonGroup({
        id,
      }),
    [deletePlatoonGroup, token],
  );

  useEffect(() => {
    if (!company?.companyId) return;

    getGroups({
      params: {
        'companyId.equals': company.companyId,
        page: page - 1,
        size: pageSize,
        sort: 'startDateFrom,desc',
      },
    });
  }, [company?.companyId, page, getGroups]);

  useEffect(() => {
    if (data?.totalCount != null) {
      setCountPages(Math.max(1, Math.ceil(data.totalCount / pageSize)));
    }
  }, [data]);

  useEffect(() => {
    if (!data?.platoonGroups) {
      setPlatoonGroups([]);
      return undefined;
    }

    let alive = true;

    const loadVehicles = async () => {
      const groupsWithVehicles = await Promise.all(
        data.platoonGroups.map(async (group) => {
          const vehicles = await getPlatoonGroupVehicles(
            { groupId: group.platoonGroupId },
            { 'status.in': ['CONFIRM', 'JOINED'] },
          );

          return {
            id: group.platoonGroupId,
            ...group,
            platoonGroupVehicles: vehicles.map((pgv) => ({
              id: pgv.platoonGrpVehicleId,
              ...pgv,
              time:
                convertDurationToSeconds(pgv.intervalNext) - convertDurationToSeconds(group.intervalDurationNoParsed),
            })),
          };
        }),
      );

      if (alive) {
        setPlatoonGroups(groupsWithVehicles);
      }
    };

    loadVehicles();

    return () => {
      alive = false;
    };
  }, [data]);

  useEffect(() => {
    if (!platoonGroups.length) {
      setDisplayedGroups([]);
      return undefined;
    }

    let alive = true;

    const loadVehicleNums = async () => {
      const vehicleIds = [...new Set(platoonGroups.flatMap((g) => g.platoonGroupVehicles.map((v) => v.vehicleId)))];

      let params = `companyId.equals=${company.companyId}&`;
      vehicleIds.forEach((id) => {
        params += `vehicleId.in=${id}&`;
      });

      const vehicles = await getVehiclesWithDtoParams(params);

      const result = platoonGroups.map((group) => {
        const finishVehicles = [];
        const noFinishVehicles = [];

        group.platoonGroupVehicles.forEach((pgv) => {
          const vehicleNum = vehicles.find((v) => v.vehicleId === pgv.vehicleId)?.num;

          const isLeader = convertDurationToSeconds(pgv.intervalLeader) === 0;

          const vehicleWithNum = { ...pgv, num: vehicleNum };

          if (isLeader) {
            finishVehicles.push(vehicleWithNum);
          } else {
            noFinishVehicles.push(vehicleWithNum);
          }
        });

        return {
          ...group,
          noFinishVehicles: [
            ...(noFinishVehicles.length ? [] : [{ id: `start-${group.id}`, simplePoint: true }]),
            ...noFinishVehicles,
            { id: `end-${group.id}`, simplePoint: true },
          ],
          finishVehicles,
        };
      });

      if (alive) {
        setDisplayedGroups(result);
      }
    };

    loadVehicleNums();

    return () => {
      alive = false;
    };
  }, [platoonGroups, company?.companyId]);

  return (
    <Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="Добавить">
          <IconButton color="primary" onClick={toCreateGroup}>
            <AddCircle />
          </IconButton>
        </Tooltip>
      </Box>

      {isLoading ? (
        <PendingData />
      ) : displayedGroups.length ? (
        <List>
          {displayedGroups.map((group) => (
            <ListItem sx={{ display: 'flex', flexDirection: 'column' }} key={group?.id}>
              <PlatoonGroup
                platoonGroup={group}
                buttons={
                  <>
                    <EditButton onEdit={() => toGroup(group.platoonGroupId)} />
                    <DeleteButton
                      onDelete={() => {
                        confirm({
                          title: 'Удаление',
                          confirmationText: 'Да',
                          cancellationText: 'Отмена',
                          description: `Вы действительно хотите удалить «${group?.name}»?`,
                        })
                          .then(() => onDelete({ id: group.platoonGroupId }))
                          .catch(() => {});
                      }}
                    />
                  </>
                }
              />
              <Divider flexItem />
            </ListItem>
          ))}
        </List>
      ) : isError ? (
        <NoData text="Произошла ошибка" />
      ) : (
        <NoData />
      )}

      {countPages > 1 && (
        <Box className={style.pagination}>
          <Pagination count={countPages} page={page} onChange={handleChangePage} />
        </Box>
      )}
    </Box>
  );
}

CompanyPlatoonGroupsWidget.propTypes = {
  company: PropTypes.shape(),
  toGroup: PropTypes.func.isRequired,
  toCreateGroup: PropTypes.func.isRequired,
};

CompanyPlatoonGroupsWidget.defaultProps = {
  company: null,
};

export default CompanyPlatoonGroupsWidget;
